import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FrontendLabelingStrategy, RegistryLabelingStrategy, TagSchema} from 'constants/RegistryConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {MixturePrepSchemaFieldSelectorsQuery} from 'notebook/structured-tables/components/modals/__generated/MixturePrepSchemaFieldSelectorsQuery';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import SchemaLabelingStrategyInput from 'registry/labeling-strategies/SchemaLabelingStrategyInput';
import {getParentLinkMixtureField} from 'registry/mixtures/ParentMixtureHelpers';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

interface Props {
  onRequestClose: () => void;
  onRequestSubmit: (
    schema: TagSchema,
    labelingStrategy: RegistryLabelingStrategy,
    parentLinkMixtureFieldId: string | null,
    selectorsById: SchemaFieldSelectorsDict
  ) => void;
  selectorsById: SchemaFieldSelectorsDict | null;
}

export const MIXTURE_PREP_SCHEMA_FIELD_SELECTORS_QUERY: DocumentNode = gql`
  query MixturePrepSchemaFieldSelectorsQuery @skipAudit @engComponent(name: REGISTRY) {
    schemaFieldSelectors {
      ...SchemaFieldSelectorGraphQL
    }
  }
  ${getFragmentWrapper('SchemaFieldSelector').fragment}
`;

function QueryingMixturePrepGroupInsertModal(props: Omit<Props, 'selectorsById'>): JSX.Element {
  return (
    <BenchlingQuery<MixturePrepSchemaFieldSelectorsQuery>
      query={MIXTURE_PREP_SCHEMA_FIELD_SELECTORS_QUERY}
      renderLoading={() => <MixturePrepGroupInsertModal {...props} selectorsById={null} />}
    >
      {({schemaFieldSelectors}) => {
        const selectorsById = schemaFieldSelectors.reduce((acc: SchemaFieldSelectorsDict, selector) => {
          acc[selector.id] = selector;
          return acc;
        }, {});

        return <MixturePrepGroupInsertModal {...props} selectorsById={selectorsById} />;
      }}
    </BenchlingQuery>
  );
}

function MixturePrepGroupInsertModal({onRequestClose, onRequestSubmit, selectorsById}: Props): JSX.Element {
  const [submitted, setSubmitted] = useState(false);
  const [selectedSchema, setSelectedSchema] = useState<TagSchema | null>(null);
  const [selectedLabelingStrategy, setSelectedLabelingStrategy] = useState<RegistryLabelingStrategy | null>(
    null
  );
  const insertionDisabled =
    submitted || selectedSchema == null || selectedLabelingStrategy == null || selectorsById == null;
  return (
    <Modal
      title="Insert mixture prep table"
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        if (submitted) {
          // prevent double clicks
          return;
        }
        setSubmitted(true);
        const maybeParentLinkMixtureField = getParentLinkMixtureField(selectedSchema!.fields);
        onRequestSubmit(
          selectedSchema!,
          selectedLabelingStrategy!,
          maybeParentLinkMixtureField?.apiId ?? null,
          selectorsById!
        );
      }}
      buttons={[{text: 'Insert', submit: true, style: ButtonStyle.SECONDARY, disabled: insertionDisabled}]}
    >
      <FormGroup>
        <FieldLabel label="Schema" />
        <TagSchemaSelector
          onChange={setSelectedSchema}
          selectedTagSchemaId={selectedSchema != null ? selectedSchema.id : null}
          oligoAwareFolderItemTypes={[ModelTypeConstants.MIXTURE]}
          tagSchemaFilterFn={(schema: TagSchema) =>
            Boolean(schema.mixtureSchemaConfig?.allowMeasuredIngredients)
          }
        />
        {!selectedSchema && (
          <FieldLabel
            className={css(styles.subLabel)}
            label="Schema must allow measured ingredients."
            labelStyle={FieldLabel.STYLES.SUBLABEL}
          />
        )}
      </FormGroup>
      {selectedSchema && (
        <FormGroup>
          <SchemaLabelingStrategyInput
            labelingStrategy={selectedLabelingStrategy}
            onChange={(labelingStrategy) => {
              setSelectedLabelingStrategy(labelingStrategy as RegistryLabelingStrategy);
            }}
            tagSchemaId={selectedSchema.id}
            bannedLabelingStrategies={[FrontendLabelingStrategy.CONVERT_IDS]}
          />
        </FormGroup>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  subLabel: {marginTop: '7px'},
});

export default QueryingMixturePrepGroupInsertModal;
